import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import DynamicDataGrid from "../components/dynamicDataGrid";
import ModalLoadingOverlay, {
  modalLoadingContainerSx,
} from "./ModalLoadingOverlay";

const columns = [
  { field: "date", headerName: "Date", flex: 1 },
  { field: "type", headerName: "Type", flex: 1 },
  { field: "entered", headerName: "Entered By", flex: 1 },
  { field: "regarding", headerName: "Regarding", flex: 1 },
  { field: "notes", headerName: "Notes", flex: 2 },
];

export default function Activities({
  activityData,
  totalRows,
  gridLoading,
  onApplyFilter,
  onResetFilter,
  regardingOptions,
  enteredByOptions,
  typeOptions,
  onRegardingChange,
  filtersLoading,
}) {
  const [openFilter, setOpenFilter] = useState(false);
  const [regarding, setRegarding] = useState("");
  const [enteredBy, setEnteredBy] = useState("");
  const [type, setType] = useState("");
  const [rows, setRows] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    if (activityData && Array.isArray(activityData)) {
      const formatted = activityData.map((item, idx) => ({
        id: idx + 1,
        date: item.Date ? new Date(item.Date).toLocaleDateString() : "NA",
        type: item.Type || "NA",
        entered: item["Entered By"] || "NA",
        regarding: item.Regarding || "NA",
        notes: item.Notes || "NA",
      }));
      setRows(formatted);
    }
  }, [activityData]);

  const handleClose = () => {
    setOpenFilter(false);
    setRegarding("");
    setEnteredBy("");
    setType("");

    const isRegarding = localStorage.getItem("regarding");
    if (onRegardingChange && !isRegarding) {
      onRegardingChange();
    }
  };

  const handleReset = () => {
    setRegarding("");
    setEnteredBy("");
    setType("");
    localStorage.setItem("regarding", "");
    localStorage.setItem("enteredBy", "");
    localStorage.setItem("type", "");
    setOpenFilter(false);

    if (onResetFilter) onResetFilter();
    if (onRegardingChange) onRegardingChange();
  };

  const handleApply = () => {
    const filters = { regarding, enteredBy, type, paginationModel };
    if (onApplyFilter) onApplyFilter(filters);
    localStorage.setItem("regarding", regarding);
    localStorage.setItem("enteredBy", enteredBy);
    localStorage.setItem("type", type);
    setOpenFilter(false);
  };

  useEffect(() => {
    localStorage.setItem("regarding", "");
    localStorage.setItem("enteredBy", "");
    localStorage.setItem("type", "");
  }, []);

  useEffect(() => {
    const filters = { regarding, enteredBy, type, paginationModel };
    if (onApplyFilter) onApplyFilter(filters);
  }, [paginationModel]);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      {/* ── Filter button row with Activities heading ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* LEFT: Activities heading */}
        <Typography
          sx={{ fontSize: "16px", fontWeight: 600, color: "#151D48" }}
        >
          Activities
        </Typography>

        {/* RIGHT: Filter button */}
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setOpenFilter(true);
            setRegarding(localStorage.getItem("regarding"));
            setEnteredBy(localStorage.getItem("enteredBy"));
            setType(localStorage.getItem("type"));
          }}
          startIcon={<FilterAltOutlinedIcon sx={{ fontSize: "16px" }} />}
          sx={{
            borderColor: "#d1d5db",
            color: "#374151",
            backgroundColor: "#fff",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            borderRadius: "8px",
            px: 1.5,
            "&:hover": {
              borderColor: "#9ca3af",
              backgroundColor: "#f9fafb",
            },
          }}
        >
          Filter
        </Button>
      </Box>

      {/* ── Filter Dialog ── */}
      <Dialog
        open={openFilter}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: modalLoadingContainerSx(filtersLoading, { borderRadius: 2 }) }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Activity Filters
          <IconButton onClick={handleClose} size="small" sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, minWidth: 120 }}
              >
                Regarding
              </Typography>
              <FormControl size="small" fullWidth>
                <InputLabel>Select Regarding</InputLabel>
                <Select
                  value={regarding}
                  label="Select Regarding"
                  onChange={(e) => {
                    const jobId = e.target.value;
                    setRegarding(jobId);
                    setEnteredBy("");
                    if (onRegardingChange) onRegardingChange(jobId);
                  }}
                >
                  {regardingOptions?.length > 0 ? (
                    regardingOptions.map((opt) => (
                      <MenuItem key={opt.job_id} value={opt.job_id}>
                        {opt.title}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No Jobs</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, minWidth: 120 }}
              >
                Entered By
              </Typography>
              <FormControl size="small" fullWidth>
                <InputLabel>Select Entered By</InputLabel>
                <Select
                  value={enteredBy}
                  label="Select Entered By"
                  onChange={(e) => setEnteredBy(e.target.value)}
                >
                  {enteredByOptions?.length > 0 ? (
                    enteredByOptions.map((opt) => (
                      <MenuItem key={opt.user_id} value={opt.user_id}>
                        {opt["Entered By"]}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No Users</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, minWidth: 120 }}
              >
                Type
              </Typography>
              <FormControl size="small" fullWidth>
                <InputLabel>Select Type</InputLabel>
                <Select
                  value={type}
                  label="Select Type"
                  onChange={(e) => setType(e.target.value)}
                >
                  {typeOptions?.length > 0 ? (
                    typeOptions.map((opt) => (
                      <MenuItem
                        key={opt.activity_type_id}
                        value={opt.activity_type_id}
                      >
                        {opt.short_description}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No Types</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Button
            onClick={handleReset}
            variant="outlined"
            color="error"
            sx={{ borderRadius: 5 }}
          >
            Reset
          </Button>
          <Box>
            <Button className="cancel-btn" onClick={handleClose} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              variant="outlined"
              disabled={!regarding && !enteredBy && !type}
              sx={{ borderRadius: 5 }}
            >
              Apply
            </Button>
          </Box>
        </DialogActions>
        <ModalLoadingOverlay open={filtersLoading} />
      </Dialog>

      {/* ── DynamicDataGrid ── */}
      <Box sx={{ height: "100%", width: "100%", overflowY: "hidden" }}>
        <DynamicDataGrid
          rows={rows}
          loading={gridLoading}
          columns={columns}
          totalRows={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={(newModel) => setPaginationModel(newModel)}
        />
      </Box>
    </Box>
  );
}
